import { GeoJsonPoint } from 'types/FarmData';

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;

// Every location in this marketplace is in Latvia. Used only as a safety net
// to recover pairs that were stored with the axes swapped (see parsePoint).
const LATVIA_LAT = { min: 55.5, max: 58.2 };
const LATVIA_LNG = { min: 20.8, max: 28.3 };

const inRange = (value: number, range: { min: number; max: number }) =>
  value >= range.min && value <= range.max;

const validLatLng = (lat: number, lng: number): LatLng | null =>
  Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
    ? { lat, lng }
    : null;

export const parsePoint = (
  value?: string | GeoJsonPoint | null
): LatLng | null => {
  if (!value) return null;

  const [a, b] = (
    typeof value === 'string'
      ? value.split(',').map(Number)
      : Array.isArray(value.coordinates)
        ? [value.coordinates[1], value.coordinates[0]]
        : []
  ) as [number, number];

  if (inRange(a, LATVIA_LAT) && inRange(b, LATVIA_LNG))
    return { lat: a, lng: b };
  if (inRange(b, LATVIA_LAT) && inRange(a, LATVIA_LNG))
    return { lat: b, lng: a };

  return validLatLng(a, b);
};

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const haversineKm = (from: LatLng, to: LatLng): number => {
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
    Math.cos(toRadians(to.lat)) *
    Math.sin(deltaLng / 2) ** 2;

  return EARTH_RADIUS_KM * 2 * Math.asin(Math.sqrt(a));
};

export const centroid = (points: LatLng[]): LatLng | null => {
  if (!points.length) return null;

  return {
    lat: points.reduce((sum, p) => sum + p.lat, 0) / points.length,
    lng: points.reduce((sum, p) => sum + p.lng, 0) / points.length,
  };
};
